import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    this.#desktopMediaQuery.addEventListener('change', this.#handleViewportChange, { signal });
    queueMicrotask(() => this.#syncGallerySlides());

    if (this.mobileProgressEnabled) {
      queueMicrotask(() => {
        this.#updateMobileProgress(this.slideshow?.current ?? 0);
      });
    }
  }

  #controller = new AbortController();
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!this.mobileProgressEnabled) return;
    this.#updateMobileProgress(event.detail?.index ?? this.slideshow?.current ?? 0);
  };

  #updateMobileProgress(index) {
    const slideCount = this.slideCount;

    if (slideCount <= 1) {
      this.style.setProperty('--mobile-carousel-progress', '100%');
      return;
    }

    const progress = ((index + 1) / slideCount) * 100;
    this.style.setProperty('--mobile-carousel-progress', `${Math.max(0, Math.min(100, progress))}%`);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    const adjustedIndex = this.#getAdjustedGalleryIndex(index);
    this.refs.zoomDialogComponent?.open(adjustedIndex, event);
  }

  #handleViewportChange = () => {
    this.#syncGallerySlides();
  };

  #syncGallerySlides() {
    const metafieldCount = this.#getMobileMetafieldCount();
    const isDesktop = this.#isDesktopGallery();

    this.querySelectorAll('.product-media-container--mobile-metafield').forEach((item) => {
      if (isDesktop) {
        item.setAttribute('hidden', '');
      } else {
        item.removeAttribute('hidden');
      }
    });

    this.querySelectorAll('.product-media-container--skip-first-on-mobile').forEach((item) => {
      if (!isDesktop && metafieldCount > 0) {
        item.setAttribute('hidden', '');
      } else {
        item.removeAttribute('hidden');
      }
    });

    this.querySelectorAll('.slideshow-controls__thumbnail--mobile-metafield').forEach((item) => {
      if (isDesktop) {
        item.setAttribute('hidden', '');
      } else {
        item.removeAttribute('hidden');
      }
    });

    this.querySelectorAll('.slideshow-controls__thumbnail--skip-first-on-mobile').forEach((item) => {
      if (!isDesktop && metafieldCount > 0) {
        item.setAttribute('hidden', '');
      } else {
        item.removeAttribute('hidden');
      }
    });

    if (!this.slideshow) return;

    queueMicrotask(() => {
      const current = this.slideshow?.current ?? 0;

      if (isDesktop && metafieldCount > 0 && current < metafieldCount) {
        this.slideshow?.select(0, undefined, { animate: false, filteredIndex: true });
      }

      if (this.mobileProgressEnabled) {
        this.#updateMobileProgress(this.slideshow?.current ?? 0);
      }

      const slideshow = this.slideshow;
      if (slideshow) {
        const current = slideshow.current;
        slideshow.current = current;
      }
    });
  }

  #isDesktopGallery() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #getMobileMetafieldCount() {
    return Number(this.dataset.mobileMetafieldCount || 0);
  }

  #getAdjustedGalleryIndex(index) {
    const metafieldCount = this.#getMobileMetafieldCount();

    if (metafieldCount === 0) return index;

    if (this.#isDesktopGallery()) {
      return Math.max(0, index - metafieldCount);
    }

    if (index < metafieldCount) return index;

    return Math.max(metafieldCount, index - 1);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  get slideCount() {
    const totalCount = Number(this.dataset.slideCount || 0);
    const metafieldCount = this.#getMobileMetafieldCount();

    if (metafieldCount === 0) return totalCount;

    if (this.#isDesktopGallery()) {
      return Math.max(0, totalCount - metafieldCount);
    }

    return Math.max(0, totalCount - 1);
  }

  get mobileProgressEnabled() {
    return this.dataset.mobileProgressEnabled === 'true';
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
